'use client'

import React, { useActionState, useEffect, useState } from 'react'
import classNames from 'classnames'

import Step from '@/step/Step'
import StepForm from '@//step-form/StepForm'
import Fieldset from '@/fieldset/Fieldset'
import Textfield from '@/forms/textfield/Textfield'
import FieldErrors from '@/forms/field-errors/FieldErrors'

import { EMPTY_FORM_STATE } from '@/lib/form/form'
import { t } from '@/lib/utils/helpers'
import { useLocale } from '@/lib/context/LocaleContext'
import { useTranslations } from '@/lib/context/TranslationsContext'
import { StepBasicProps } from '@/lib/types/types'
import { useQueryState } from 'nuqs'
import { trimWhitespaces } from '@/stories/utilities/helpers/helpers'
import { getEmailLoginLink, validateEmailLoginLink } from '@/app/utils/selfServiceActions'
import { useQuery } from '@tanstack/react-query'

interface Res {
    contract: Contract[];
}

interface Contract {
    contract_no: string;
    contract_pin: string;
}

const LoginLink =  (props: StepBasicProps): JSX.Element => {
    const translations = useTranslations();
    const intro = translations.intro;
    const globals = translations.globals;
    const validationTranslations = translations.validationErrors
    const locale = useLocale()
    const linkLabel = t(intro.loginLink.tryAnotherEmail, locale)
    const [url, setUrl] = useState<string>('')
    useEffect(() => {setUrl(window?.location?.href)}, [])

    useEffect(() => {
        const params = new URLSearchParams(window.location.search)
        const returnUrl = params.get('returnUrl')
        if (returnUrl && returnUrl.startsWith('/')) {
            localStorage.setItem('returnUrl', returnUrl)
        }
    }, [])

    const [value, setValue] = useState('')
 
    const [state, formAction] = useActionState((state: any, payload: any) => getEmailLoginLink(state, payload, 'login-link', locale, url), EMPTY_FORM_STATE)

    const [emailStatus, setEmailStatus] = useQueryState('status', { history: 'push' })
    const [hashCode, setHash] = useQueryState('hash', { history: 'push' })
    const [data, setData] = useState<Res | null>(null)

    const { isLoading, error } = useQuery({
        queryKey: ['emailLoginLink'],
        queryFn: async () => {
            const data = await validateEmailLoginLink(hashCode!, locale)

            return data || null
        },
        enabled: !!hashCode,
        gcTime: 0,
        staleTime: Infinity,
        refetchOnMount: 'always',
    })

    useEffect(() => {
        if (error) console.error('Error fetching data:', error)
    }, [error])

    const validationErrors = state?.fieldErrors || props?.validationErrors;
    const className = classNames('find-policy', props.className);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const rawValue = event.target.value;

        const sanitizedValue = trimWhitespaces(rawValue);

        setValue(sanitizedValue);
    };

    return (
        <>
            {isLoading && (
                <span className='modal-upload__loader h-loader'></span>
            )}
            {!isLoading && (
                <Step
                    className={className}
                    title={data ? t(intro.loginLink.contracts, locale) : t(intro.loginLink.title, locale)}
                    description={data ? t(intro.loginLink.contractsDescription, locale) : t(intro.loginLink.description, locale)}
                    gapSize='large'
                >
                    <StepForm className='find-policy__form' action={formAction} submitLabel={t(globals.buttons.submit.label, locale)} alternativeButton={`/${locale}?step=find-policy`} linkLabel={linkLabel} submitDisabled={emailStatus === 'success'}>
                        <Fieldset>
                            <Textfield
                                label={t(intro.loginLink.email, locale)}
                                name='email'
                                isInvalid={!!(state?.fieldErrors?.['email'])}
                                onChange={handleInputChange}
                                value={value}
                            />
                            {validationErrors && (
                                <FieldErrors fieldErrors={validationErrors} />
                            )}
                            {emailStatus === 'success' && (
                                <p className='field-errors field-errors-success'>{[t(intro.loginLink.emailSent, locale)]}</p>
                            )}
                            {emailStatus === 'failed' && (
                                <FieldErrors fieldErrors={{ message: [t(intro.loginLink.emailNotSent, locale)] }} />
                            )}
                            {emailStatus === 'expired' && (
                                <FieldErrors fieldErrors={{ message: [t(intro.loginLink.expiredLink, locale)] }} />
                            )}
                            {emailStatus === 'throttle' && (
                                <FieldErrors fieldErrors={{ message: [t(validationTranslations.rateLimit, locale)] }} />
                            )}
                        </Fieldset>
                    </StepForm>
                </Step> 
            )}
        </>
    )
}

export default LoginLink